import { Text, Button, useToast, VStack } from "@chakra-ui/react";
import OlaggFormSelect from "../../../components/Forms/OlaggFormSelect";
import { AdminUserEndpoints, fetcher } from "@olagg/api-hooks";
import OlaggFormInput from "../../../components/Forms/OlaggFormInput";
import { useEffect } from "react";
import OlaggForm from "../../../components/Forms/OlaggForm";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuthStore } from "@olagg/store";

const schema = yup.object().shape({
    userId: yup.string().required('Usuario es requerido'),
    notes: yup.string().optional(),
    expiresAt: yup.string().optional(),
    sourceType: yup.string().default('User'),
    sourceId: yup.number().default(4),
    quantity: yup.number().min(1, 'La cantidad debe ser al menos 1').when('$isEditing', {
        is: false,
        then: (schema) => schema.required('Cantidad es requerida'),
        otherwise: (schema) => schema.optional()
    }),
});

interface TreasureOpportunityFormData {
    userId: string;
    notes?: string;
    expiresAt?: string;
    sourceType: string;
    sourceId: number;
    quantity?: number;
}

interface TreasureOpportunityFormProps {
    onSubmit: (data: TreasureOpportunityFormData) => void;
    initialData?: Partial<TreasureOpportunityFormData>;
    isEditing?: boolean;
}

const TreasureOpportunityForm = ({ onSubmit, initialData, isEditing = false }: TreasureOpportunityFormProps) => {
    const toast = useToast();
    const { me } = useAuthStore();

    const userOptions = async (name?: string) =>
        fetcher(AdminUserEndpoints.all({ page: 1, limit: 5, query: name }))
            .then(response => response.users.map((user: any) => ({
                label: `ID: ${user.id} - ${user.name} - ${user.email}`,
                value: user.id.toString()
            })))
            .catch(error => {
                console.error('Error fetching users:', error);
                toast({
                    title: 'Error',
                    description: 'Error al cargar usuarios',
                    status: 'error',
                    position: 'bottom-right',
                    duration: 3000,
                });
                return [];
            });

    const useFormMethods = useForm<TreasureOpportunityFormData>({
        mode: 'onChange',
        resolver: yupResolver(schema),
        context: { isEditing },
        defaultValues: {
            userId: '',
            notes: '',
            expiresAt: '',
            sourceType: 'User',
            sourceId: me?.id,
            quantity: isEditing ? undefined : 1,
            ...initialData
        }
    });

    const handleSubmit = (data: TreasureOpportunityFormData) => {
        try {
            onSubmit(data);
        } catch (error: any) {
            toast({
                title: 'Error',
                description: error.message || 'Error al procesar el formulario',
                status: 'error',
                position: 'bottom-right',
                duration: 3000,
            });
        }
    };

    useEffect(() => {
        if (initialData) {
            useFormMethods.reset({
                ...initialData,
                sourceType: 'User',
                sourceId: me?.id,
                quantity: isEditing ? undefined : (initialData.quantity || 1),
            });
        }
    }, [initialData, useFormMethods, isEditing]);

    return (
        <VStack spacing={4} w="full" maxW="600px">
            <OlaggForm
                useFormMethods={useFormMethods}
                onSubmit={handleSubmit}
            >

                <OlaggFormSelect
                    label="Usuario"
                    controlName="userId"
                    required={true}
                    newVersion
                    staticOptions={initialData?.userId ? [{ 
                        label: `Usuario seleccionado`, 
                        value: initialData.userId 
                    }] : []}
                    asyncOptions={userOptions}
                    isSearchable
                    placeholder="Buscar y seleccionar usuario"
                />

                {!isEditing && (
                    <OlaggFormInput
                        label="Cantidad"
                        controlName="quantity"
                        placeholder="Número de oportunidades extra a crear"
                        required={true}
                    />
                )}

                <OlaggFormInput
                    label="Notas (opcional)"
                    controlName="notes"
                    placeholder="Agregar notas sobre esta oportunidad extra"
                    required={false}
                />

                <OlaggFormInput
                    label="Fecha de expiración (opcional)"
                    controlName="expiresAt"
                    placeholder="YYYY-MM-DD HH:MM"
                    required={false}
                />

                {/* Campos ocultos */}
                <input type="hidden" {...useFormMethods.register('sourceType')} />
                <input type="hidden" {...useFormMethods.register('sourceId')} />

                <Button
                    w="full"
                    variant="filledGradient"
                    fontSize="16px"
                    fontWeight="600"
                    cursor="pointer"
                    type="submit"
                    isLoading={useFormMethods.formState.isSubmitting}
                    loadingText={isEditing ? "Actualizando..." : "Creando..."}
                >
                    {isEditing ? 'Actualizar Oportunidad' : 'Crear Oportunidad Extra'}
                </Button>
            </OlaggForm>
        </VStack>
    );
};

export default TreasureOpportunityForm;
